import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { Button, Card, Col, Modal, Row, Tab, Tabs } from "react-bootstrap";
import {
  FaAtom,
  FaBriefcase,
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaTwitter,
} from "react-icons/fa";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import api from "../../apiClient";
import ResumeSelectionPopup from "../../Dashboards/CandidateDashboardpages/ResumeSelectionPopup";
import Error404 from "../Error404";
import Footer from "../Footer";
import "./Company.css";
import CompanyJobs from "./CompanyJobs";
import CompanyOverView from "./CompanyOverView";

/* ------------------------------------------------------------------ */
/* SEO helpers (no extra dependency needed)                            */
/* ------------------------------------------------------------------ */
const upsertMeta = (name, content) => {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const upsertJsonLd = (id, json) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  // Escape "<" so company text can never close the script tag
  el.textContent = json.replace(/</g, "\\u003c");
};

const removeById = (id) => {
  const el = document.getElementById(id);
  if (el) el.remove();
};

const isHttpUrl = (u) => typeof u === "string" && /^https?:\/\//i.test(u);

const EachCompanyPage = () => {
  const location = useLocation();
  const { state } = location;
  const { companyId, scrollToJobs } = state || {};
  const [activeTab, setActiveTab] = useState("overview");
  const [company, setCompany] = useState(null);
  const [countOfApplications, setCountOfApplications] = useState(0);
  const [countOfHR, setCountOfHR] = useState(0);
  const [countOfJobs, setCountOfJobs] = useState(0);
  const [companyLogo, setCompanyLogo] = useState("");
  const [companyBanner, setCompanyBanner] = useState("");
  const { companyName } = useParams();

  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [countOfTotalJobs, setCountOfTotalJobs] = useState();
  const [companyWebsite, setCompanyWebsite] = useState("");

  const navigate = useNavigate();

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState();
  const [userName, setUserName] = useState(null);
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  };
  useEffect(() => {
    const loggedInUser = getCookie("user");
    let userObject = null;
    try {
      userObject = loggedInUser ? JSON.parse(loggedInUser) : null;
    } catch {
      userObject = null;
    }
    if (userObject) {
      setIsLoggedIn(true);

      setUser(userObject);
      if (userObject.userRole === "Candidate") {
        setUserId(userObject.userId);
        setUserName(userObject.userName);
      }
    }
  }, []);
  const jobsSectionRef = useRef(null);

  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const activeTabParam = params.get("activetab");

    if (scrollToJobs) {
      setActiveTab("jobs");
      if (jobsSectionRef.current) {
        jobsSectionRef.current.scrollIntoView({ behavior: "smooth" });
      }
    } else if (activeTabParam) {
      setActiveTab(activeTabParam);
    } else {
      setActiveTab("overview");
    }
    // checkCompanyExists() calls fetchCompany() itself, so no direct call here
    fetchData();
    checkCompanyExists(companyName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName, scrollToJobs]);

  // Check if company exists in the database
  const checkCompanyExists = async (companyName) => {
    try {
      const response = await api.checkCompanyByName(companyName);
      if (response.status === 200) {
        setError(null);
        fetchCompany(companyName);
      } else {
        setError("Company not found in Jobbox");
      }
    } catch (error) {
      setError("Company not found in Jobbox");
    }
  };

  const fetchCompany = async (companyName) => {
    try {
      const response = await api.getCompanyByCompanyName(companyName);
      const companyData = response.data;

      setCompany(companyData);
      setCompanyLogo(companyData.companyLogo);
      setCompanyBanner(companyData.companyBanner);
      fetchCompanyLogo(companyData.companyName);
      fetchCompanyBanner(companyData.companyName);
      fetchSocialMediaLinks(companyData.companyName);
    } catch (error) {}
  };

  const fetchData = async () => {
    try {
      const fetchCountOfApplicationByCompany =
        await api.getCountOfApplicationsByCompany(companyName);
      setCountOfApplications(fetchCountOfApplicationByCompany.data);

      const fetchCountOfHRByCompany =
        await api.getCountOfHRByCompany(companyName);
      setCountOfHR(fetchCountOfHRByCompany.data);

      const countOfJobsByCompany =
        await api.getCountOfActiveJobsByCompany(companyName);
      setCountOfJobs(countOfJobsByCompany.data);

      const fetchCountOfTotalJobsByCompany =
        await api.getCountOfTotalJobsByCompany(companyName);
      setCountOfTotalJobs(fetchCountOfTotalJobsByCompany.data);
    } catch (error) {}
  };

  const fetchCompanyLogo = async (companyName) => {
    try {
      const response = await api.getLogo(companyName);
      const image = `data:image/jpeg;base64,${btoa(
        new Uint8Array(response.data).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          "",
        ),
      )}`;
      setCompanyLogo(image);
    } catch (error) {}
  };

  const fetchCompanyBanner = async (companyName) => {
    try {
      const response = await api.getBanner(companyName);
      const image = `data:image/jpeg;base64,${btoa(
        new Uint8Array(response.data).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          "",
        ),
      )}`;
      setCompanyBanner(image);
    } catch (error) {}
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    if (tab === "jobs" && jobsSectionRef.current) {
      jobsSectionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };
  useEffect(() => {
    if (scrollToJobs) {
      setActiveTab("jobs");
      if (jobsSectionRef.current) {
        jobsSectionRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [scrollToJobs]);
  const openModal = (content) => {
    setModalContent(content);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const handleHRClick = () => {
    openModal("hr");
  };

  const handleCandidateClick = () => {
    openModal("candidate");
    localStorage.setItem("redirectAfterLogin", "dream-company");
  };
  const [resumeId, setResumeId] = useState(0);
  const [selectedResume, setSelectedResume] = useState(null);

  const handleResumeSelect = async (resume) => {
    const resumeId = resume.target.value;
    setSelectedResume(resumeId);
    if (resumeId) {
      setResumeId(resumeId);
      setShowResumePopup(false);
    }
  };
  const handleModalOptionClick = (option) => {
    closeModal();

    if (option === "login") {
      if (modalContent === "hr") {
        navigate("/hr-sign-in", { state: { userType: "HR" } });
      } else if (modalContent === "candidate") {
        navigate("/signin", {
          state: {
            userType: "Candidate",
            companyId: companyId,
            companyName: company?.companyName || companyName,
          },
        });
      }
    } else if (option === "register") {
      if (modalContent === "hr") {
        navigate("/hr-signup", {
          state: { companyName: companyName, companyWebsite: companyWebsite },
        });
      } else {
        navigate("/candidate-signup", { state: { userType: "Candidate" } });
      }
    }
  };

  const [socialMediaLinks, setSocialMediaLinks] = useState({
    facebookLink: "",
    twitterLink: "",
    instagramLink: "",
    linkedinLink: "",
  });

  const fetchSocialMediaLinks = async (companyName) => {
    try {
      const response = await api.getSocialMediaLinks(companyName);
      const { facebookLink, twitterLink, instagramLink, linkedinLink } =
        response.data;
      setSocialMediaLinks({
        facebookLink,
        twitterLink,
        instagramLink,
        linkedinLink,
      });
    } catch (error) {}
  };

  const [showResumePopup, setShowResumePopup] = useState(false);
  const [resumes, setResumes] = useState([]);
  const [hasDreamApplied, setHasDreamApplied] = useState(false);

  useEffect(() => {
    if (userId) {
      api
        .getResume(userId)
        .then((response) => {
          setResumes(response.data);
        })
        .catch((error) => {});
    }
  }, [userId]);
  const BASE_API_URL = process.env.REACT_APP_API_URL;
  const applyJob = async (resumeId, userId, companyName) => {
    if (!userId) {
      Swal.fire({
        icon: "error",
        title: "Login Required",
        text: "Please login to apply for a job.",
      });
      return;
    }

    let loadingPopup;

    try {
      loadingPopup = Swal.fire({
        title: "Applying to the job...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const appliedOn = new Date();
      const year = appliedOn.getFullYear();
      const month = String(appliedOn.getMonth() + 1).padStart(2, "0");
      const day = String(appliedOn.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;

      const response = await axios.put(
        `${BASE_API_URL}/applyDreamCompany?userId=${userId}&companyName=${companyName}&formattedDate=${formattedDate}&resumeId=${resumeId}`,
      );

      if (response.data) {
        Swal.close();
        await Swal.fire({
          icon: "success",
          title: "Apply Successful!",
          text: "You have successfully applied for this Company.",
        });
        setHasDreamApplied(true);
      }
    } catch (error) {
      if (loadingPopup) {
        Swal.close();
      }
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Something went wrong! Please try again later.",
      });
    } finally {
      if (loadingPopup) {
        Swal.close();
      }
    }
  };

  useEffect(() => {
    if (userId && companyName) {
      checkHasUserDreamApplied();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName, userId]);
  const checkHasUserDreamApplied = async () => {
    try {
      if (!userId || !companyName) return;
      const response = await api.checkDreamCompanyAppliedOrNot(
        userId,
        companyName,
      );
      setHasDreamApplied(response.data);
    } catch (error) {}
  };

  const handleApplyCompany = () => {
    if (companyName.trim() === "") {
      toast.error("Please enter the company name before selecting a resume.");
      return;
    } else if (!resumeId) {
      toast.error("Please select a resume.");
      return;
    }
    applyJob(resumeId, userId, companyName);
  };

  const customTabHeader = (title, icon) => (
    <div className="d-flex align-items-center">
      <span className="me-2">{icon}</span>
      <span className="fs-6 fw-bold">{title}</span>
    </div>
  );

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (companyName) {
      const fetchDocuments = async () => {
        setLoading(true);
        try {
          const response = await api.getPolicyDocuments(companyName);
          if (response.data) {
            setDocuments(response.data);
          }
        } catch (err) {
        } finally {
          setLoading(false);
        }
      };

      fetchDocuments();
    }
  }, [companyName]);

  const [reapplyMonths, setReapplyMonths] = useState(12);

  /* ---------------------------------------------------------------- */
  /* Display logo (UI only) vs schema logo (must be public https URL)  */
  /* ---------------------------------------------------------------- */
  const DEFAULT_COMPANY_LOGO =
    "https://static.vecteezy.com/system/resources/previews/013/899/376/original/cityscape-design-corporation-of-buildings-logo-for-real-estate-business-company-vector.jpg";
  // company can be null on first render, so use optional chaining
  const displayCompanyLogo =
    companyLogo || company?.companyImageUrl || DEFAULT_COMPANY_LOGO;

  const schemaLogo = [company?.companyImageUrl, company?.logo].find(isHttpUrl);
  const orgUrl = [
    companyWebsite,
    company?.website,
    company?.companyWebsite,
  ].find(isHttpUrl);
  const sameAs = Object.values(socialMediaLinks).filter(isHttpUrl);
  const orgDescription =
    company?.description || company?.companyDescription || company?.aboutCompany;

  // email / telephone intentionally left out: add them only if they are public
  const companyStructuredData = company
    ? {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: company.companyName || companyName,
        ...(orgUrl ? { url: orgUrl } : {}),
        ...(schemaLogo ? { logo: schemaLogo } : {}),
        ...(orgDescription ? { description: orgDescription } : {}),
        ...(sameAs.length ? { sameAs } : {}),
      }
    : null;

  const schemaJson = companyStructuredData
    ? JSON.stringify(companyStructuredData)
    : "";

  // Title, description, canonical and JSON-LD go into <head>
  useEffect(() => {
    if (error) {
      upsertMeta("robots", "noindex");
      removeById("organization-jsonld");
      return;
    }
    const name = company?.companyName || companyName;
    document.title = `Jobbox.one | Company Page - ${name}`;
    if (company) {
      const desc = String(orgDescription || `Jobs and details for ${name}`)
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 155);
      upsertMeta("description", desc);
      upsertCanonical(`${window.location.origin}${window.location.pathname}`);
      upsertMeta("robots", "index, follow");
    }
    if (schemaJson) {
      upsertJsonLd("organization-jsonld", schemaJson);
    } else {
      removeById("organization-jsonld");
    }
    return () => removeById("organization-jsonld");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemaJson, error, companyName, company?.companyName]);

  useEffect(() => {
    if (companyName) {
      api
        .getHiringPolicy(companyName)
        .then((response) => {
          if (response.data) {
            const months = response.data.allowReapply
              ? response.data.reapplyMonths
              : 12;
            setReapplyMonths(months || 12);
          }
        })
        .catch((error) => {});
    }
  }, [companyName]);

  return (
    <div>
      <div>
        <div>
          {error ? (
            <div style={{ textAlign: "center", margin: "20px 0" }}>
              <Error404 />
            </div>
          ) : (
            <div style={{ overflowX: "hidden" }}>
              <Row style={{ marginBottom: "20px" }}>
                <div>
                  <Card
                    style={{
                      width: "100%",
                      height: "60%",
                      borderRadius: "10px",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    }}
                  >
                    <Card.Body style={{ padding: 0, position: "relative" }}>
                      <div
                        style={{
                          position: "relative",
                          height: "auto",
                          maxHeight: "55%",
                        }}
                      >
                        <img
                          src={
                            companyBanner ||
                            "https://cdn.pixabay.com/photo/2016/04/20/07/16/logo-1340516_1280.png"
                          }
                          alt="Company Banner"
                          className="banner-image"
                          style={{
                            width: "100%",
                            height: "auto",
                            maxHeight: "200px",
                            objectFit: "cover",
                            cursor: "pointer",
                            borderTopLeftRadius: "10px",
                            borderTopRightRadius: "10px",
                          }}
                        />
                      </div>

                      <div
                        style={{
                          position: "absolute",
                          top: "95%",
                          left: "50px",
                          transform: "translateY(-50%)",
                        }}
                      >
                        <img
                          src={displayCompanyLogo}
                          alt="Company Logo"
                          className="logo-image"
                          style={{
                            width: "20vw",
                            height: "20vw",
                            maxWidth: "150px",
                            maxHeight: "150px",
                            cursor: "pointer",
                            objectFit: "cover",
                            clipPath: "none",
                            border: "5px solid #f0f0f0",
                            borderRadius: "10px",
                          }}
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </div>
                <h2 className="responsive-title">
                  <b>{companyName.toUpperCase()}</b>
                </h2>
                <span className="responsive-socials">
                  {socialMediaLinks.facebookLink && (
                    <a
                      href={socialMediaLinks.facebookLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaFacebook
                        size={28}
                        style={{ margin: "0 5px", color: "#3b5998" }}
                      />
                    </a>
                  )}
                  {socialMediaLinks.twitterLink && (
                    <a
                      href={socialMediaLinks.twitterLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaTwitter
                        size={28}
                        style={{ margin: "0 5px", color: "#1da1f2" }}
                      />
                    </a>
                  )}
                  {socialMediaLinks.instagramLink && (
                    <a
                      href={socialMediaLinks.instagramLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaInstagram
                        size={28}
                        style={{ margin: "0 5px", color: "#e4405f" }}
                      />
                    </a>
                  )}
                  {socialMediaLinks.linkedinLink && (
                    <a
                      href={socialMediaLinks.linkedinLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaLinkedin
                        size={28}
                        style={{ margin: "0 5px", color: "#0077b5" }}
                      />
                    </a>
                  )}
                </span>
              </Row>
              <hr style={{ border: "1px solid black", marginTop: "0rem" }} />
              <Row style={{ marginBottom: "0", paddingBottom: "0" }}>
                <Col style={{ marginLeft: "10px" }}>
                  <Tabs
                    activeKey={activeTab}
                    onSelect={(key) => handleTabClick(key)}
                    style={{ marginBottom: "20px" }}
                  >
                    <Tab
                      eventKey="overview"
                      title={customTabHeader("About  ", <FaAtom />)}
                    ></Tab>
                    <Tab
                      eventKey="jobs"
                      title={customTabHeader("Jobs  ", <FaBriefcase />)}
                    ></Tab>
                  </Tabs>
                </Col>
              </Row>
              <Row style={{ marginBottom: "0", paddingBottom: "0" }}>
                {/* Main Content */}
                <Col xs={12} md={9} style={{ marginBottom: "20px" }}>
                  {activeTab === "overview" && <CompanyOverView />}
                  {activeTab === "jobs" && <CompanyJobs />}
                </Col>

                {/* Sidebar */}
                <Col xs={12} md={3} className="company-page-key-stats">
                  {/* Key Stats Card */}
                  <Card
                    className="key-stats"
                    style={{
                      width: "100%",
                      marginBottom: "20px",
                      borderRadius: "10px",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    }}
                  >
                    <Card.Body>
                      {isLoggedIn ? (
                        user?.userRole === "HR" ? null : user?.userRole ===
                          "Candidate" ? (
                          hasDreamApplied ? (
                            <p
                              style={{
                                color: "#28a745",
                                fontSize: "18px",
                                fontWeight: "bold",
                                backgroundColor: "#e9f5e9",
                                padding: "10px",
                                borderRadius: "5px",
                                textAlign: "left",
                                margin: "10px 0",
                                boxShadow: "rgba(0, 0, 0, 0.1)",
                                width: "100px",
                              }}
                            >
                              Applied
                            </p>
                          ) : (
                            <>
                              <div className="resume-dropdown-container">
                                {resumes.length === 0 ? (
                                  <div>
                                    <p className="text-danger">
                                      Please add your resume first.
                                    </p>
                                    <Link
                                      to="/candidate-dashboard/resume/resumeAdd"
                                      state={{
                                        userName: userName,
                                        userId: userId,
                                      }}
                                    >
                                      <Button variant="primary">
                                        ADD RESUME
                                      </Button>
                                    </Link>
                                  </div>
                                ) : (
                                  <>
                                    <h5 className="fw-bold">Select Resume</h5>

                                    <select
                                      id="resumeSelect"
                                      value={selectedResume || ""}
                                      onChange={handleResumeSelect}
                                      required
                                      className="form-select"
                                    >
                                      <option value="">Select Resume</option>
                                      {resumes.map((resume) => (
                                        <option
                                          key={resume.id}
                                          value={resume.id}
                                        >
                                          {resume.message}
                                        </option>
                                      ))}
                                    </select>
                                  </>
                                )}
                              </div>

                              {resumes.length > 0 && (
                                <Button
                                  variant="success"
                                  onClick={handleApplyCompany}
                                  disabled={!selectedResume}
                                >
                                  Apply
                                </Button>
                              )}
                            </>
                          )
                        ) : (
                          <Button
                            variant="primary"
                            style={{ marginRight: "12px" }}
                            onClick={handleHRClick}
                          >
                            Claim/Login
                          </Button>
                        )
                      ) : (
                        <>
                          <div style={{ marginBottom: "10px" }}>
                            <label>
                              <strong>For HR - </strong>
                            </label>
                            <Button variant="primary" onClick={handleHRClick}>
                              Claim/Login
                            </Button>
                          </div>

                          <div style={{ marginBottom: "10px" }}>
                            <label>
                              <strong>For Applicant - </strong>{" "}
                            </label>
                            <Button
                              variant="success"
                              onClick={handleCandidateClick}
                            >
                              Login to Apply
                            </Button>
                          </div>
                        </>
                      )}

                      <div style={{ marginBottom: "16px" }}>
                        <h5>Applicants: {countOfApplications}</h5>
                      </div>
                      <div style={{ marginBottom: "16px" }}>
                        <h5>HR mapped: {countOfHR > 0 ? "Yes" : "No"}</h5>
                        <h5>Total HR 's: {countOfHR}</h5>
                      </div>
                      <div style={{ marginBottom: "16px" }}>
                        <h5>Total Jobs: {countOfTotalJobs}</h5>
                      </div>
                      <div style={{ marginBottom: "16px" }}>
                        <h5>Key Stats:</h5>
                        <ul>
                          <li>Total Active Jobs: {countOfJobs}</li>
                        </ul>
                      </div>
                    </Card.Body>
                  </Card>

                  {/* Company Policies Card */}
                  <Card
                    className="key-stats"
                    style={{
                      width: "100%",
                      borderRadius: "10px",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    }}
                  >
                    <Card.Body>
                      <h3>
                        <b>Company Policies</b>
                      </h3>
                      <h4>Job Reapply Policy</h4>
                      <p>
                        Candidates can reapply after{" "}
                        <strong>{reapplyMonths}</strong> months.
                      </p>
                      {documents.length === 0 ? (
                        <p>No documents available for this company.</p>
                      ) : (
                        <div>
                          {documents.map((document) => (
                            <div
                              key={document.documentId}
                              style={{ marginBottom: "10px" }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <strong>{document.documentTitle}:</strong>
                                <a
                                  href={`data:application/octet-stream;base64,${document.documentFile}`}
                                  download={document.documentTitle}
                                  className="btn btn-primary"
                                  style={{ marginLeft: "10px" }}
                                >
                                  Download
                                </a>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row style={{ marginTop: "10px" }}>
                <Footer />
              </Row>
            </div>
          )}

          {showResumePopup && (
            <ResumeSelectionPopup
              resumes={resumes}
              onSelectResume={handleResumeSelect}
              show={true}
              onClose={() => setShowResumePopup(false)}
              userName={userName}
              userId={userId}
            />
          )}
          {/* Modal for Apply button */}
          <Modal show={showModal} onHide={closeModal}>
            <Modal.Header
              closeButton
              style={{
                backgroundColor: "#faccc",
                color: "white",
                borderBottom: "none",
              }}
            >
              <Modal.Title>Choose an Option</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: "20px", textAlign: "center" }}>
              {(modalContent === "hr" || modalContent === "candidate") && (
                <>
                  <Button
                    variant="primary"
                    onClick={() => handleModalOptionClick("login")}
                    style={{
                      width: "100%",
                      marginBottom: "10px",
                      backgroundColor: "#6c5ce7",
                      borderColor: "#6c5ce7",
                    }}
                  >
                    Already have an account - Login
                  </Button>
                  <Button
                    variant="success"
                    onClick={() => handleModalOptionClick("register")}
                    style={{
                      width: "100%",
                      backgroundColor: "#00b894",
                      borderColor: "#00b894",
                    }}
                  >
                    Don't have an account - Register
                  </Button>
                </>
              )}
            </Modal.Body>
          </Modal>
        </div>
        <div>
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
          />
        </div>
      </div>
    </div>
  );
};

export default EachCompanyPage;